"use client";

import { Button } from "@/components/ui/button";
import { TooltipContent } from "@/components/ui/tooltip-content";
import { cn } from "@/lib/utils";
import { SendIcon } from "lucide-react";
import { useState } from "react";
import {
	BlueskyIcon,
	BlueskyShareButton,
	EmailIcon,
	EmailShareButton,
	FacebookIcon,
	FacebookMessengerIcon,
	FacebookMessengerShareButton,
	FacebookShareButton,
	FacebookShareCount,
	LinkedinIcon,
	LinkedinShareButton,
	RedditIcon,
	RedditShareButton,
	RedditShareCount,
	XIcon as SocialXIcon,
	TelegramIcon,
	TelegramShareButton,
	TwitterShareButton,
	WhatsappIcon,
	WhatsappShareButton,
} from "react-share";

interface SocialShareProps {
	url: string;
	title: string;
}

export function SocialShare({ url, title }: SocialShareProps) {
	const [open, setOpen] = useState(false);

	return (
		<div className="relative flex flex-col gap-2 w-full justify-end transition-all">
			<TooltipContent content="Share to your friends!">
				<Button
					onClick={() => setOpen(!open)}
					className={cn(
						"inline-flex w-fit self-end gap-1 items-center text-sm bg-primary text-background rounded-full px-2.5 py-1",
						"ring-offset-2 transition duration-200 hover:ring-2 hover:ring-green-500",
					)}
				>
					<SendIcon className="size-4" /> Share
				</Button>
			</TooltipContent>

			{open && <SocialShareButtons url={url} title={title} />}
		</div>
	);
}

export function SocialShareButtons({ url, title }: SocialShareProps) {
	return (
		<div className="flex flex-row w-full py-1 gap-1 items-start justify-between">
			<div className="flex flex-row gap-1 items-center justify-center">
				<FacebookShareButton
					url={url}
					className="cursor-pointer hover:opacity-75"
				>
					<FacebookIcon size={32} round />
				</FacebookShareButton>

				<div className="font-extrabold text-primary">
					<FacebookShareCount
						url={url}
						className="inline-flex items-center justify-center text-nowrap text-xs"
					>
						{(count) => count}
					</FacebookShareCount>
				</div>
			</div>

			<div className="flex flex-row gap-1 items-center justify-center">
				<FacebookMessengerShareButton
					url={url}
					appId="521270401588372"
					className="cursor-pointer hover:opacity-75"
				>
					<FacebookMessengerIcon size={32} round />
				</FacebookMessengerShareButton>
			</div>

			<div className="flex flex-row gap-1 items-center justify-center">
				<TwitterShareButton
					url={url}
					title={title}
					className="cursor-pointer hover:opacity-75"
				>
					<SocialXIcon size={32} round />
				</TwitterShareButton>
			</div>

			<div className="flex flex-row gap-1 items-center justify-center">
				<TelegramShareButton
					url={url}
					title={title}
					className="cursor-pointer hover:opacity-75"
				>
					<TelegramIcon size={32} round />
				</TelegramShareButton>
			</div>

			<div className="flex flex-row gap-1 items-center justify-center">
				<WhatsappShareButton
					url={url}
					title={title}
					separator=":: "
					className="cursor-pointer hover:opacity-75"
				>
					<WhatsappIcon size={32} round />
				</WhatsappShareButton>
			</div>

			<div className="flex flex-row gap-1 items-center justify-center">
				<LinkedinShareButton
					url={url}
					className="cursor-pointer hover:opacity-75"
				>
					<LinkedinIcon size={32} round />
				</LinkedinShareButton>
			</div>

			<div className="flex flex-row gap-1 items-center justify-center">
				<RedditShareButton
					url={url}
					title={title}
					windowWidth={660}
					windowHeight={460}
					className="cursor-pointer hover:opacity-75"
				>
					<RedditIcon size={32} round />
				</RedditShareButton>

				<div className="font-extrabold text-primary">
					<RedditShareCount
						url={url}
						className="inline-flex items-center justify-center text-nowrap text-xs"
					/>
				</div>
			</div>

			<div className="flex flex-row gap-1 items-center justify-center">
				<BlueskyShareButton
					url={url}
					title={title}
					windowWidth={660}
					windowHeight={460}
					className="cursor-pointer hover:opacity-75"
				>
					<BlueskyIcon size={32} round />
				</BlueskyShareButton>
			</div>

			<div className="flex flex-row gap-1 items-center justify-center">
				<EmailShareButton
					url={url}
					subject={title}
					body="Read more at "
					className="cursor-pointer hover:opacity-75"
				>
					<EmailIcon size={32} round />
				</EmailShareButton>
			</div>
		</div>
	);
}
